/**
 * musion-stem-separator.js — page-side facade of the MUSION separator
 * (`separate.onnx`, HTDemucs-style: drums / bass / other / vocals; weights
 * CC BY 4.0, Zenodo 18630002 — attribution is REQUIRED wherever this engine is
 * user-facing, see docs/MUSION-LICENSE.txt and the `model_credit` keys).
 *
 * Rebuilt 2026-09-19. Until then this file WAS the engine: ORT on the WASM
 * provider, on the main thread — a standby lever nobody had timed. Timed, it
 * is 22 s of blocked page per 7.8 s segment (a quarter of an hour of frozen tab
 * per song) against 0.41 s on the WebGPU provider. So the model now runs in
 * musion-worker.js, on the provider that passes a known-answer test, and this
 * file only: decides whether the device can take it (support), fetches the
 * model through the shared resumable downloader, talks to the worker, and
 * hands the stems back in the CALLER's sample rate. The recipe itself lives in
 * musion-shared.js — one copy for the worker and the unit tests.
 *
 * Measured quality (MUSDB18-test, 50 clips, SI-SDR, sepbench/dfm 2026-09-19):
 * drums 9.00 dB / «song without drums» 13.72 dB — within 0.3–0.5 dB of the
 * htdemucs_ft the paid server lane runs (9.28 / 14.19).
 *
 * Public API (a superset of what config/separators.php consumers call):
 *   support()                         → Promise<{ok, reason?}>   cheap, downloads nothing
 *   preload(slug, fnOrOpts)           → Promise<{provider, segMs}>
 *   split(audioBuffer, {onProgress, onBeat, signal, slug, stems, allowWasm})
 *       → Promise<{vocal, instrumental, drums, bass, other}>  (only what `stems`
 *         asked for; AudioBuffers in the caller's rate and length)
 *       onBeat() — after every run of the model (a segment, a probe, the known-answer
 *         test): the sign of life a «separator went silent» watchdog counts
 *   isWarm() → bool        release() → terminates the worker (the only way wasm
 *                                      memory is ever given back)
 *
 * Wiring comes from the page, never from literals here: the worker URL and the
 * model URL pair are <meta> tags rendered by tools/partials/musion-engine, the
 * ORT build is window.__ortBuild.nativeGpu() (OrtBuildSingleOwnerTest).
 */
(function () {
'use strict';

var SR = 44100;
var SOURCES = ['drums', 'bass', 'other', 'vocals'];
var MIN_DEVICE_GB = 4;          // weights ≈160 MB on the GPU + the same in wasm + activations

function meta(name) { var m = document.querySelector('meta[name="' + name + '"]'); return (m && m.content) || ''; }
function abs(u) { try { return new URL(u, window.location.href).href; } catch (_) { return u; } }
function tele(slug, action, payload) {
    try { if (typeof window.__ae === 'function') window.__ae('feature_use', Object.assign({ slug: slug || 'musion-stem-separator', action: action }, payload || {})); } catch (_) {}
}
function typed(code, message) { var e = new Error(message || code); e.code = code; return e; }

function loadScript(path, probe) {
    if (probe()) return Promise.resolve();
    return new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        s.src = window.__jsSrc ? window.__jsSrc(path) : path;
        s.onload = function () { probe() ? resolve() : reject(typed('dep_missing', path + ' loaded without its global')); };
        s.onerror = function () { reject(typed('dep_missing', path + ' failed to load')); };
        document.head.appendChild(s);
    });
}
function needVerifier() { return loadScript('/js/ort-ep-verify.js', function () { return !!window.OrtEpVerify; }); }
function needDownloader() { return loadScript('/js/model-download.js', function () { return !!self.__mdFetchBytes; }); }

// ── Can this device take it? Decided BEFORE a byte of the model moves. ──
var adapterInfo = null, cacheKey = null;
function support() {
    if (!window.Worker) return Promise.resolve({ ok: false, reason: 'no_worker' });
    if (!meta('musion-worker-url') || !meta('musion-model-url')) return Promise.resolve({ ok: false, reason: 'not_wired' });
    if (!window.__ortBuild || typeof window.__ortBuild.nativeGpu !== 'function') return Promise.resolve({ ok: false, reason: 'not_wired' });
    var build = window.__ortBuild.nativeGpu();
    if (!build) return Promise.resolve({ ok: false, reason: 'webkit' });      // the owner answers null on WebKit only
    if (!navigator.gpu) return Promise.resolve({ ok: false, reason: 'no_webgpu' });
    var mem = navigator.deviceMemory;
    if (typeof mem === 'number' && mem < MIN_DEVICE_GB) return Promise.resolve({ ok: false, reason: 'low_memory' });
    return needVerifier().then(function () {
        return window.OrtEpVerify.adapterIdentity();
    }).then(function (id) {
        adapterInfo = id || null;
        if (!id || !id.present) return { ok: false, reason: 'no_webgpu' };
        // A software adapter (SwiftShader) «has WebGPU» and runs this model slower
        // than the CPU provider would.
        if (id.fallback) return { ok: false, reason: 'software_gpu' };
        var ua = (navigator.userAgent.match(/(?:Chrome|Firefox|Edg)\/(\d+)/) || [])[1] || '0';
        cacheKey = window.OrtEpVerify.verdictKey(['musion', 'ort-native', id.vendor, id.architecture, 'b' + ua]);
        if (window.OrtEpVerify.isRejected(cacheKey, 'webgpu')) return { ok: false, reason: 'gpu_rejected' };
        return { ok: true, adapter: id };
    }, function () { return { ok: false, reason: 'no_webgpu' }; });
}

// ── Worker life-cycle ──
var worker = null, ready = null, readyInfo = null, pending = {}, seq = 0, currentSlug = null, lastPlace = null;
// who wants to hear that the model is working: every run of it — a segment, a probe, the known-answer test —
// ends in a `beat` from the worker (musion-worker.js alive()); split({ onBeat }) listens while it waits
var beatHooks = [];
// who waits for the model download: the page often STARTS it early (a prewarm, with no one to tell), and a split
// that arrives meanwhile used to hear nothing of it — on a phone 84 MB over the air is a minute or two of silence
// (preload 141 s on the field's Android, 18.10.2026), which a «separator went silent» watchdog read as stuck
var loadHooks = [];

function release() {
    if (worker) { try { worker.terminate(); } catch (_) {} }
    worker = null; ready = null; readyInfo = null;
    Object.keys(pending).forEach(function (id) { try { pending[id].reject(typed('released', 'separator released')); } catch (_) {} });
    pending = {};
}

function onVerdict(v) {
    var flat = window.OrtEpVerify ? window.OrtEpVerify.telemetryOf(v, adapterInfo) : { ep: v && v.provider, ok: v && v.ok ? 1 : 0 };
    flat.why = v && v.why || null; flat.worst_db = v && v.worstDb; flat.sum_db = v && v.sumDb; flat.seg_ms = v && v.segMs || null;
    // the four stem levels of the known-answer segment (drums, bass, other, vocals):
    // what a re-calibration of MusionShared.KAT_REF_DB would be read from
    if (v && v.levels) flat.lv = [v.levels.drums, v.levels.bass, v.levels.other, v.levels.vocals];
    tele(currentSlug, 'musion_verdict', flat);
    // Only a WRONG ANSWER is remembered (30 days): a throw or a cancel is a bad day.
    if (v && !v.ok && !v.error && !v.skipped && v.provider === 'webgpu' && cacheKey && window.OrtEpVerify) {
        try { window.OrtEpVerify.remember(cacheKey, 'webgpu', { kind: 'kat', why: v.why || null }); } catch (_) {}
    }
}

function preload(slug, fnOrOpts) {
    var opts = typeof fnOrOpts === 'function' ? { onProgress: fnOrOpts } : (fnOrOpts || {});
    currentSlug = slug || currentSlug;
    if (ready) return ready;
    var t0 = performance.now();
    ready = support().then(function (sup) {
        if (!sup.ok && !(opts.allowWasm && sup.reason !== 'not_wired' && sup.reason !== 'no_worker' && sup.reason !== 'webkit')) {
            throw typed('musion_unsupported_' + sup.reason, 'MUSION cannot run here: ' + sup.reason);
        }
        var providers = sup.ok ? ['webgpu'] : [];
        if (opts.allowWasm) providers.push('wasm');
        return needDownloader().then(function () {
            var urls = [meta('musion-model-url'), meta('musion-model-url-fallback')].filter(Boolean);
            var tryUrl = function (i) {
                return self.__mdFetchBytes(urls[i], {
                    signal: opts.signal,
                    onProgress: function (got, total) {
                        if (!(total > 0)) return;
                        var f = Math.min(1, got / total);
                        if (opts.onProgress) opts.onProgress(f, 'model');
                        loadHooks.slice().forEach(function (h) { try { h(f); } catch (_) {} });
                    }
                }).catch(function (e) {
                    if (i + 1 < urls.length && !(opts.signal && opts.signal.aborted)) { tele(slug, 'musion_model_fallback', { reason: String(e && e.message || e).slice(0, 80) }); return tryUrl(i + 1); }
                    throw e;
                });
            };
            return tryUrl(0);
        }).then(function (bytes) {
            var buf = bytes.buffer.byteLength === bytes.length ? bytes.buffer : bytes.slice().buffer;
            var build = window.__ortBuild.nativeGpu();
            return new Promise(function (resolve, reject) {
                try { worker = new Worker(meta('musion-worker-url')); }
                catch (e) { reject(typed('worker_failed', String(e && e.message || e))); return; }
                worker.onerror = function (ev) {
                    var err = typed('worker_failed', (ev && ev.message) || 'worker error');
                    reject(err);
                    Object.keys(pending).forEach(function (id) { pending[id].reject(err); });
                    pending = {};
                };
                worker.onmessage = function (ev) {
                    var m = ev.data || {};
                    if (m.type === 'verdict') onVerdict(m.verdict);
                    else if (m.type === 'ready') { readyInfo = { provider: m.provider, segMs: m.segMs, unverified: !!m.unverified }; resolve(readyInfo); }
                    else if (m.type === 'progress') { var p = pending[m.id]; if (p && p.onProgress) p.onProgress(m.done, m.of, m.route); }
                    else if (m.type === 'beat') beatHooks.slice().forEach(function (f) { try { f(m.id); } catch (_) {} });
                    else if (m.type === 'result') { var r = pending[m.id]; if (r) { delete pending[m.id]; r.resolve(m); } }
                    else if (m.type === 'error') {
                        var err = typed(m.code || 'musion_error', m.message);
                        err.verdicts = m.verdicts || null;
                        if (m.id != null && pending[m.id]) { var q = pending[m.id]; delete pending[m.id]; q.reject(err); }
                        else reject(err);
                    }
                };
                worker.postMessage({
                    type: 'init', modelBytes: buf, providers: providers,
                    ortUrl: abs(build.url), wasmBase: abs(build.wasmBase),
                    ortFallbackUrl: abs(build.fallbackUrl), fallbackWasmBase: abs(build.fallbackWasmBase)
                }, [buf]);
            });
        });
    }).then(function (info) {
        tele(slug, 'musion_preload_ok', { ms: Math.round(performance.now() - t0), ep: info.provider, seg_ms: info.segMs || 0 });
        return info;
    }).catch(function (e) {
        tele(slug, 'musion_preload_fail', { code: e && e.code || null, msg: String(e && e.message || e).slice(0, 80) });
        release();
        throw e;
    });
    return ready;
}

// ── Audio in / out: the model's 44.1 kHz is ITS passport, not the caller's format ──
function renderAt(buffer, rate, length) {
    var Ctor = window.OfflineAudioContext || window.webkitOfflineAudioContext;
    var ctx = new Ctor(2, length, rate);
    var node = ctx.createBufferSource();
    node.buffer = buffer; node.connect(ctx.destination); node.start();
    return ctx.startRendering();
}
function toStereo44k(audioBuffer) {
    var p = audioBuffer.sampleRate === SR ? Promise.resolve(audioBuffer)
        : renderAt(audioBuffer, SR, Math.max(1, Math.round(audioBuffer.duration * SR)));
    return p.then(function (b) {
        var L = new Float32Array(b.getChannelData(0));
        var R = new Float32Array(b.getChannelData(b.numberOfChannels > 1 ? 1 : 0));
        return [L, R];
    });
}
function toCallerBuffer(pair, audioBuffer) {
    var Ctor = window.OfflineAudioContext || window.webkitOfflineAudioContext;
    var n = pair[0].length;
    var src = new Ctor(2, n, SR).createBuffer(2, n, SR);
    src.getChannelData(0).set(pair[0]); src.getChannelData(1).set(pair[1]);
    if (audioBuffer.sampleRate === SR && audioBuffer.length === n) return Promise.resolve(src);
    // Resampling is linear, so a sum of stems stays the sum (the contract the
    // sibling engine keeps — tests/playwright/vocal-remover-stem-rate-contract.mjs).
    return renderAt(src, audioBuffer.sampleRate, audioBuffer.length);
}

// A cancel is the person's decision and takes effect NOW: the promise rejects at once and the
// worker is terminated. Waiting for the worker to acknowledge meant waiting out the segment it was
// on (0.4 s on a gaming card, tens of seconds on a weak one) and left it `busy` for the next press;
// terminating also hands the model's memory back, which a cancelled run has no more use for.
function abortable(promise, signal) {
    if (!signal) return promise;
    return new Promise(function (resolve, reject) {
        var stop = function () { reject(new DOMException('aborted', 'AbortError')); release(); };
        if (signal.aborted) { stop(); return; }
        signal.addEventListener('abort', stop, { once: true });
        promise.then(function (v) { signal.removeEventListener('abort', stop); resolve(v); },
                     function (e) { signal.removeEventListener('abort', stop); reject(e); });
    });
}

/* The facade's PUBLIC vocabulary. `instrumental` is ours (drums+bass+other summed); the rest are
   the model's sources, with `vocals` — the model's own spelling and every page's — accepted for
   `vocal`. Declared here so the filter and the result check read the same list. */
var PUBLIC_STEMS = ['vocal', 'instrumental', 'drums', 'bass', 'other'];
var STEM_ALIAS = { vocals: 'vocal' };

function split(audioBuffer, opts) {
    opts = opts || {};
    var slug = opts.slug, onProgress = opts.onProgress || null, signal = opts.signal || null;
    /* What the caller may ask for, and what it gets back under that very name.
       ⚠️ `vocals` is an ALIAS of `vocal` and not a courtesy: it is the model's own source name and
       the name every page uses for that track, and until 2026-09-19 asking by it returned nothing
       at all — the stem was quietly left out of `want`, so it was never computed and never
       mentioned. A separator that hands back three stems when four were asked for, without a word,
       is worse than one that refuses. Hence the alias AND the two checks below. */
    var asked = [], canon = {}, unknown = [];
    (function () {
        var raw = (opts.stems && opts.stems.length) ? opts.stems : PUBLIC_STEMS;
        for (var i = 0; i < raw.length; i++) {
            var c = STEM_ALIAS[raw[i]] || raw[i];
            if (PUBLIC_STEMS.indexOf(c) < 0) { unknown.push(raw[i]); continue; }
            if (asked.indexOf(c) < 0) asked.push(c);
            canon[c] = raw[i];                       // give it back spelled the way it was asked for
        }
    })();
    if (unknown.length) {
        var ue = new Error('unknown stem(s): ' + unknown.join(', '));
        ue.code = 'unknown_stem';
        return Promise.reject(ue);
    }
    var want = SOURCES.filter(function (s) {
        var key = s === 'vocals' ? 'vocal' : s;
        return asked.indexOf(key) >= 0 || (asked.indexOf('instrumental') >= 0 && s !== 'vocals');
    });
    var t0 = performance.now();
    // a sign of life on every run of the model while this split waits — the known-answer test of a
    // provider still being verified included (the facade has one worker; the hook goes with the split)
    var onBeat = typeof opts.onBeat === 'function' ? opts.onBeat : null;
    if (onBeat) beatHooks.push(onBeat);
    // the model's download reaches this split whoever started it (loadHooks) — its own preload call asks for none
    var onLoad = onProgress ? function (f) { onProgress(f * 0.3, 'model'); } : null;
    if (onLoad) loadHooks.push(onLoad);
    var unhookLoad = function () { var j = loadHooks.indexOf(onLoad); if (j >= 0) loadHooks.splice(j, 1); };
    var unhook = function () { var i = beatHooks.indexOf(onBeat); if (i >= 0) beatHooks.splice(i, 1); unhookLoad(); };
    return abortable(preload(slug, {
        signal: signal, allowWasm: !!opts.allowWasm
    }).then(function () {
        unhookLoad();
        return toStereo44k(audioBuffer);
    }).then(function (chans) {
        if (signal && signal.aborted) throw new DOMException('aborted', 'AbortError');
        var id = ++seq;
        return new Promise(function (resolve, reject) {
            pending[id] = {
                resolve: resolve, reject: reject,
                // `route: 'channels'` — the record keeps its low end to one side and each channel is
                // being separated on its own: three passes instead of one, which is what `of` counts.
                onProgress: function (done, of, route) { if (onProgress) onProgress(0.3 + 0.7 * done / of, 'separate ' + done + '/' + of, { done: done, of: of, route: route || null }); }
            };
            worker.postMessage({ type: 'split', id: id, L: chans[0], R: chans[1], want: want }, [chans[0].buffer, chans[1].buffer]);
        });
    }), signal).then(function (m) {
        var out = {}, jobs = [];
        var give = function (key, pair) {
            var name = canon[key] || key;
            jobs.push(toCallerBuffer(pair, audioBuffer).then(function (b) { out[name] = b; }));
        };
        if (asked.indexOf('instrumental') >= 0) {
            var n = m.stems.drums[0].length, instr = [new Float32Array(n), new Float32Array(n)];
            ['drums', 'bass', 'other'].forEach(function (s) { for (var c = 0; c < 2; c++) { var a = m.stems[s][c], d = instr[c]; for (var i = 0; i < n; i++) d[i] += a[i]; } });
            give('instrumental', instr);
        }
        SOURCES.forEach(function (s) { var key = s === 'vocals' ? 'vocal' : s; if (asked.indexOf(key) >= 0 && m.stems[s]) give(key, m.stems[s]); });
        return Promise.all(jobs).then(function () {
            // The promise a caller can rely on: everything it asked for is in the answer. A missing
            // key here means the recipe changed underneath, and it must stop the run, not the page.
            var missing = asked.map(function (k) { return canon[k] || k; }).filter(function (n) { return !out[n]; });
            if (missing.length) {
                var me = new Error('stems missing from the result: ' + missing.join(', '));
                me.code = 'stems_missing';
                throw me;
            }
            // Where the record keeps its low end and which run each stem came from (the worker's
            // two readings of placement). Kept on info() for the page; the stems object stays stems only.
            lastPlace = m.place || null;
            var pl = m.place || {}, ps = pl.stems || {};
            tele(slug, 'musion_split_ok', {
                sec: Math.round(audioBuffer.duration), ms: Math.round(performance.now() - t0), segments: m.segments, ep: m.provider, stems: want.length,
                low_side: pl.low_side != null ? pl.low_side : null, route: pl.route || null, passes: m.passes || 1,
                bass_side: ps.bass ? ps.bass.side : null, voc_side: ps.vocals ? ps.vocals.side : null
            });
            unhook();
            return out;
        });
    }).catch(function (e) {
        unhook();
        if ((signal && signal.aborted) || (e && (e.name === 'AbortError' || e.code === 'cancelled' || e.code === 'released'))) throw new DOMException('aborted', 'AbortError');
        tele(slug, 'musion_split_fail', { code: e && e.code || null, msg: String(e && e.message || e).slice(0, 80) });
        // A provider that stopped answering correctly is not coming back in this tab.
        if (e && e.code === 'gpu_output_corrupt') release();
        throw e;
    });
}

/* How much audio this device may hand to split() in one go. Memory, not compute,
   is the limit: the worker holds the model (~1.4 GB with its arenas) plus FOUR
   stereo stems at 44.1 kHz (~230 MB per minute of audio) — config/separators.php,
   measured 2026-09-19. One minute per gigabyte of the page's memory budget, 3…10
   minutes (`ceilSec` lowers the ceiling). stem-split-browser.js applies the same
   rule on its own; it predates this helper. */
function maxSeconds(ceilSec) {
    var B = window.__memBudget;
    var gb = B ? B.gb() : 4;
    return Math.max(180, Math.min(ceilSec || 600, Math.round(gb * 60)));
}

/* Is the model already in this browser, i.e. will split() download nothing?
   The service worker files /vendor/onnx/*.onnx under a cache of ITS choosing,
   and the page does not own that choice — so every cache is searched, by file
   name (the URL's host and query differ between the CDN and the origin). */
function modelCached() {
    if (typeof caches === 'undefined' || !caches.keys) return Promise.resolve(false);
    var file = (meta('musion-model-url').split('?')[0].split('/').pop()) || '';
    if (!file) return Promise.resolve(false);
    return caches.keys().then(function (names) {
        return Promise.all(names.map(function (n) {
            return caches.open(n).then(function (c) { return c.keys(); }).catch(function () { return []; });
        }));
    }).then(function (lists) {
        for (var i = 0; i < lists.length; i++) {
            for (var j = 0; j < lists[i].length; j++) {
                var u = String((lists[i][j] && lists[i][j].url) || '').split('?')[0];
                if (u.slice(-file.length - 1) === '/' + file) return true;
            }
        }
        return false;
    }).catch(function () { return false; });
}

window.MusionStemSeparator = {
    support: support,
    preload: preload,
    split: split,
    release: release,
    maxSeconds: maxSeconds,
    modelCached: modelCached,
    isWarm: function () { return !!readyInfo; },
    info: function () { return readyInfo; },
    /** Placement of the LAST split: { low_side, route:'stereo'|'channels', stems? } — survives release(). */
    lastPlace: function () { return lastPlace; }
};
})();
